import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import {
  FileData,
  ChonkyActions,
  FileBrowser,
  FileContextMenu,
  FileList,
  FileNavbar,
  FileToolbar,
  ChonkyIconName,
  FileActionHandler,
} from '.';
import {
  fileBrowserSlice,
  selectFiles,
  useGetFolderQuery,
  useLazyGetFileQuery,
  useCreateFileMutation,
  useCreateFolderMutation,
  FileInfo,
  useCopyFileMutation,
  useDeleteFileMutation,
  useDeleteFolderMutation,
} from '../../store/api/file.api';
import './chonky.css';
import * as Yup from 'yup';
import { JFormikDialogProps } from '../forms/jformik/jformik.types';
import { JFormikDialog } from '../forms/jformik/DialogForm';
import { FormikHelpers } from 'formik/dist/types';
import { InputAdornment } from '@mui/material';
import { Loading } from '../Loading';
import { useDispatch, useSelector } from 'react-redux';
import { ChonkyIconFA } from './chonky-icon-fontawesome';
import { setChonkyDefaults } from '.';
import { BackdropLoading } from '../BackdropLoading';

setChonkyDefaults({ iconComponent: ChonkyIconFA });
export interface ChonkyFileBrowserProps {
  files?: FileInfo;
  setFileParams?: Function;
  id: number;
}

export const ChonkyFileBrowser = ({
  id: project_id,
}: ChonkyFileBrowserProps) => {
  const dispatch = useDispatch();
  const inputFile = useRef<HTMLInputElement>(null);
  const { isLoading: filesLoading } = useGetFolderQuery(
    { project_id: project_id, folder_path: '/' },
    { refetchOnMountOrArgChange: true, skip: !(project_id && project_id > 0) }
  );
  const [download] = useLazyGetFileQuery({ refetchOnFocus: false });
  const [uploadFile] = useCreateFileMutation();
  const [copyFile, copyFileResult] = useCopyFileMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [deleteFolder] = useDeleteFolderMutation();
  const [createFolder, createFolderResult] = useCreateFolderMutation();
  const [folderPath, setFolderPath] = useState<string>('/');
  const [folderChain, setFolderChain] = useState<FileData[]>([
    { id: '/', name: 'Home', isDir: true, openable: true, loading: false },
  ]);

  const createFolderChain = ({
    folder_path,
    loading = false,
  }: {
    folder_path: string;
    loading?: boolean;
  }) => {
    let folders: FileData[] = [
      { id: '/', name: 'Home', isDir: true, openable: true },
    ];
    let absolutepath = '/';
    for (let folder of folder_path.split('/').filter((x: string) => x !== '')) {
      absolutepath += folder + '/';
      folders.push({
        id: absolutepath,
        name: folder,
        isDir: true,
        openable: true,
      });
    }
    if (loading) folders[folders.length - 1].loading = true;
    return folders;
  };
  useEffect(() => {
    if (folderPath) {
      setFolderChain(createFolderChain({ folder_path: folderPath }));
    }
  }, [folderPath]);
  const [open, setOpen] = useState<boolean>(false);
  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };
  const createFolderProps: JFormikDialogProps = useMemo(
    () => ({
      title: 'Create a folder',
      button: 'Create',
      close: onClose,
      mutation: [
        (values: any, formik: FormikHelpers<any>) => {
          createFolder({
            ...values,
            folder_path: `${folderPath}${values?.folder_name}/.hidden`,
            folder_name: values?.folder_name,
          });
          onClose(null, 'submit');
        },
        createFolderResult,
      ],
      useJFormik: () => ({
        jformikProps: {
          initialValues: () => ({ project_id }),
          validationSchema: Yup.object({
            folder_name: Yup.string().required('Please specify a folder name'),
          }),
          form: [
            {
              name: 'folder_name',
              itemType: 'textfield',
              label: 'Folder Name',
              gridProps: { sx: { pt: '30px !important' } },
            },
          ],
        },
        loading: false,
      }),
    }),
    [project_id, createFolder, createFolderResult, folderPath]
  );

  const renameProps = useCallback(
    (
      folder_path: string,
      src: string,
      name: string,
      isDir: true
    ): JFormikDialogProps => {
      const fileExtension = name.split('.').slice(-1);
      const fileNameWithoutExtension = name.replace(/\.\w*?$/, '');
      return {
        title: 'Rename',
        button: 'Save',
        close: onClose,
        mutation: [
          (values: any, formik: FormikHelpers<any>) => {
            copyFile(
              isDir
                ? {
                    project_id,
                    srcDir: src,
                    dstDir: `${folder_path}${values?.name}/`,
                    cpyDir: 'no',
                  }
                : {
                    project_id,
                    src,
                    dst: fileExtension
                      ? `${folder_path}${values?.name}.${fileExtension}`
                      : `${folder_path}${values?.name}`,
                    cpy: 'no',
                  }
            );
            onClose(null, 'submit');
          },
          copyFileResult,
        ],
        useJFormik: () => ({
          jformikProps: {
            initialValues: () =>
              fileExtension && !isDir
                ? { name: fileNameWithoutExtension }
                : { name },
            validationSchema: Yup.object({
              name: Yup.string().required('Please specify a new name'),
            }),
            form:
              fileExtension && !isDir
                ? [
                    {
                      name: 'name',
                      itemType: 'textfield',
                      label: 'Name',
                      endAdornment: (
                        <InputAdornment position="end">{` .${fileExtension}`}</InputAdornment>
                      ),
                    },
                  ]
                : [{ name: 'name', itemType: 'textfield', label: 'Name' }],
          },
          loading: false,
        }),
      };
    },
    [copyFile, copyFileResult, project_id]
  );

  const [dialogProps, setDialogProps] =
    useState<JFormikDialogProps>(createFolderProps);

  const myFileActions = [
    {
      ...ChonkyActions.CreateFolder,
      requresSelection: false,
      button: {
        name: 'Add folder',
        toolbar: true,
        contextMenu: true,
        tooltip: 'Create a folder',
        icon: ChonkyIconName.folderCreate,
      },
    },
    {
      ...ChonkyActions.UploadFiles,
      button: {
        name: 'Upload',
        toolbar: true,
        tooltip: 'Upload File',
        icon: ChonkyIconName.upload,
      },
    },
    {
      ...ChonkyActions.DownloadFiles,
      notAllowedWhenDir: true,
      button: {
        name: 'Download',
        toolbar: false,
        contextMenu: true,
        group: '',
        icon: ChonkyIconName.download,
      },
    },
    {
      ...ChonkyActions.DeleteFiles,
      button: {
        name: 'Delete',
        toolbar: false,
        contextMenu: true,
        group: '',
        icon: ChonkyIconName.trash,
      },
    },
    {
      ...ChonkyActions.SortFilesByName,
      button: {
        name: '',
        toolbar: false,
        group: '',
      },
    },
    {
      ...ChonkyActions.SortFilesBySize,
      button: {
        name: '',
        toolbar: false,
        group: '',
      },
    },
    {
      ...ChonkyActions.SortFilesByDate,
      button: {
        name: '',
        toolbar: false,
        group: '',
      },
    },
    {
      ...ChonkyActions.ToggleShowFoldersFirst,
      button: {
        name: '',
        toolbar: false,
        group: '',
      },
    },
    {
      id: 'rename_selection',
      requiresSingleSelection: true,
      button: {
        name: 'Rename',
        toolbar: false,
        contextMenu: true,
        group: '',
        icon: ChonkyIconName.symlink,
      },
    },
    {
      id: 'drop_file',
    },
  ];

  const actionsToDisable: string[] = [
    ChonkyActions.EnableGridView.id,
    ChonkyActions.EnableListView.id,
    ChonkyActions.SelectAllFiles.id,
    ChonkyActions.EnableCompactView.id,
    ChonkyActions.ClearSelection.id,
    ChonkyActions.ToggleHiddenFiles.id,
    ChonkyActions.ToggleDarkMode.id,
    ChonkyActions.OpenSelection.id,
    ChonkyActions.CopyFiles.id,
  ];

  const onSelectFiles = async (event: any) => {
    const filelist = (event.target as HTMLInputElement).files;
    if (filelist) uploadFiles(filelist, folderPath);
  };

  const uploadFiles = useCallback(
    async (filelist: FileList, destination: string) => {
      let files = [];
      let filesData: FileData[] = [];
      if (filelist) {
        for (let i = 0; i < filelist.length; i++) {
          let file = filelist[i];
          if (file) {
            files.push({
              file: file,
              filename: file.name,
            });
            filesData.push({
              id: destination + file.name,
              name: file.name,
              loading: true,
            });
          }
        }
      }
      await Promise.all(
        files.map(
          attachment =>
            attachment.file &&
            uploadFile({
              project_id,
              file: attachment.file,
              file_type: attachment.file.type,
              file_size: attachment.file.size,
              file_name: attachment.filename,
              file_path: destination,
            })
        )
      );
    },
    [uploadFile, project_id]
  );

  const deleteFiles = useCallback(
    async (files: FileData[]) => {
      dispatch(fileBrowserSlice.actions.setFilesLoading(files));
      for (let file of files) {
        if (file.isDir && file.id) {
          deleteFolder({ project_id, folder_path: file.id });
        } else if (file.id) {
          deleteFile({ project_id, file_path: file.id });
        }
      }
    },
    [deleteFile, deleteFolder, project_id, dispatch]
  );

  const moveFiles = useCallback(
    async (destination: FileData, files: FileData[]) => {
      dispatch(fileBrowserSlice.actions.setFilesLoading(files));
      await Promise.all(
        files.map(file => {
          if (file.isDir) {
            const srcDir = file.id;
            const dstDir = destination.id + file.name + '/';
            return copyFile({ project_id, srcDir, dstDir, cpy: 'no' });
          } else {
            const src = file.id;
            const dst = destination.id + file.name;
            return copyFile({ project_id, src, dst, cpy: 'no' });
          }
        })
      );
    },
    [copyFile, project_id, dispatch]
  );

  const handleAction = useCallback<FileActionHandler>(
    async data => {
      return new Promise(() => {
        switch (data.id) {
          case ChonkyActions.DownloadFiles.id:
            for (const file of data.state.selectedFiles) {
              if (!file.isDir)
                download({ project_id: project_id, file_path: file.id });
            }
            break;
          case ChonkyActions.CreateFolder.id:
            setDialogProps(createFolderProps);
            setOpen(true);
            break;
          case ChonkyActions.UploadFiles.id:
            if (inputFile) {
              inputFile?.current?.click();
            }
            break;
          case ChonkyActions.OpenFiles.id:
            if (data.payload.targetFile?.isDir) {
              setFolderPath(data.payload.targetFile.id);
            } else if (data.payload.targetFile?.id) {
              download({
                project_id: project_id,
                file_path: data.payload.targetFile.id,
              });
            }
            break;
          case ChonkyActions.EndDragNDrop.id:
            if (data.payload.destination.isDir) {
              if (data.payload.selectedFiles.length > 0) {
                moveFiles(data.payload.destination, data.payload.selectedFiles);
              } else {
                if (
                  data.payload.draggedFile.isDir &&
                  data.payload.destination.isDir
                ) {
                  const srcDir = data.payload.draggedFile.id;
                  const dstDir =
                    data.payload.destination.id +
                    data.payload.draggedFile.name +
                    '/';
                  copyFile({ project_id, srcDir, dstDir, cpy: 'no' });
                } else if (
                  !data.payload.draggedFile.isDir &&
                  data.payload.destination.isDir
                ) {
                  const src = data.payload.draggedFile.id;
                  const dst =
                    data.payload.destination.id + data.payload.draggedFile.name;
                  copyFile({ project_id, src, dst, cpy: 'no' });
                }
              }
            }
            break;
          case ChonkyActions.DeleteFiles.id:
            if (data.state.selectedFilesForAction.length > 0) {
              deleteFiles(data.state.selectedFilesForAction);
            } else if (
              data.state.contextMenuTriggerFile?.isDir &&
              data.state.contextMenuTriggerFile?.id
            ) {
              deleteFolder({
                project_id,
                folder_path: data.state.contextMenuTriggerFile?.id,
              });
            } else if (data.state.contextMenuTriggerFile?.id) {
              deleteFile({
                project_id,
                file_path: data.state.contextMenuTriggerFile.id,
              });
            }
            break;
          // @ts-ignore
          case 'rename_selection':
            // @ts-ignore
            setDialogProps(
              renameProps(
                folderPath,
                // @ts-ignore
                data?.state?.contextMenuTriggerFile?.id,
                // @ts-ignore
                data.state.contextMenuTriggerFile.name,
                // @ts-ignore
                data.state.contextMenuTriggerFile.isDir
              )
            );
            setOpen(true);
            break;
          // @ts-ignore
          case 'drop_file':
            // @ts-ignore
            const filelist = data?.payload?.files;
            // @ts-ignore
            const destination = data?.payload?.destination?.isDir
              ? // @ts-ignore
                data?.payload?.destination?.id
              : folderPath;
            if (filelist) uploadFiles(filelist, destination);
            break;
          default:
            break;
        }
      });
    },
    [
      project_id,
      download,
      setDialogProps,
      folderPath,
      renameProps,
      moveFiles,
      uploadFiles,
      deleteFile,
      deleteFolder,
      copyFile,
      createFolderProps,
      deleteFiles,
    ]
  );

  useEffect(() => {
    // TODO move to dialog handling by not passing through usestate, just usememo
    if (createFolderResult.fulfilledTimeStamp) {
      createFolderResult.reset();
      setOpen(false);
      // close('none', 'formSubmitted')
    } else if (copyFileResult.fulfilledTimeStamp) {
      copyFileResult.reset();
      setOpen(false);
    }
  }, [
    createFolderResult.fulfilledTimeStamp,
    copyFileResult.fulfilledTimeStamp,
    setOpen,
    copyFileResult,
    createFolderResult,
  ]);

  const browserFiles = useSelector(selectFiles);

  return (
    <>
      <FileBrowser
        files={browserFiles ? Object.values(browserFiles) : []}
        fileActions={myFileActions}
        disableDefaultFileActions={actionsToDisable}
        onFileAction={handleAction}
        folderChain={folderChain}
      >
        <FileNavbar />
        <FileToolbar />
        {filesLoading ? <BackdropLoading relative /> : <FileList />}
        <FileContextMenu />
      </FileBrowser>
      <JFormikDialog open={open} {...{ maxWidth: 'xs', ...dialogProps }} />
      <input
        multiple
        type="file"
        id="file"
        ref={inputFile}
        style={{ display: 'none' }}
        onChange={onSelectFiles}
      />
    </>
  );
};
